import { TranslocoService } from '@jsverse/transloco';

import { ChannelAnalogInputUseMode, ChannelGroup } from '@shure/shared/angular/data-access/system-api/models';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';

export interface SiblingChannelsDevice {
	features: {
		audioChannels: SiblingChannel[];
	};
}

interface SiblingChannel {
	id: string;
	group: ChannelGroup;
	features: { name?: string; analogInputUse?: { mode?: ChannelAnalogInputUseMode } };
}

/**
 * put all direct channel groups here for build channel names
 * it gets channel name from mappedInputGroups
 * for example, DirectOutput channel name is built from Generic channel name
 */
export const directChannelGroups: ChannelGroup[] = [
	ChannelGroup.DirectOutputDanteMicInput,
	ChannelGroup.DirectOutput,
	ChannelGroup.DirectOutputAnalogInput
];
export const mappedInputGroups: ChannelGroup[] = [
	ChannelGroup.DanteMicInput,
	ChannelGroup.Generic,
	ChannelGroup.AnalogInput
];

/**
 * Internal wrapper class for buildDirectChannelName() so it can access safely local translation labels
 */
@shUseTranslation('scDevUiSharedServices', (lang) => import(`../../../i18n/${lang}`))
class BuildDirectChannelNameWrapper {
	public static buildDirectChannelName(
		device: SiblingChannelsDevice,
		channelId: string,
		transloco: TranslocoService
	): string {
		const correspondingInputChannel = getCorrespondingInputChannel(device, channelId);
		if (!correspondingInputChannel) {
			return '';
		}
		return `${correspondingInputChannel.features.name} (${transloco.translate(
			'scDevUiSharedServices.direct-channels.label.direct'
		)})`;
	}
}

/**
 *
 * @param device
 * @param channelId
 * @param transloco
 * @returns direct channel name
 *
 * for direct channel(DirectOutput, DirectOutputAnalogInput type), there is no name property,  so its name is constructed from
 * its corresponding input channel(Generic, AnalogInput type).
 * for apx its direct channel name in this format:  "${input channel name} + (Direct)"
 */
export function buildDirectChannelName(
	device: SiblingChannelsDevice,
	channelId: string,
	transloco: TranslocoService
): string {
	return BuildDirectChannelNameWrapper.buildDirectChannelName(device, channelId, transloco);
}

export function getCorrespondingInputChannel(device: SiblingChannelsDevice, channelId: string): SiblingChannel | null {
	for (const [index, group] of directChannelGroups.entries()) {
		const directChannelIndex = device.features.audioChannels
			.filter((chn) => chn.group === group)
			.findIndex((chn) => chn.id === channelId);
		if (directChannelIndex === -1) {
			continue;
		}
		const inputChannels = device.features.audioChannels.filter((chn) => chn.group === mappedInputGroups[index]);
		const returnChannel = inputChannels.length > directChannelIndex ? inputChannels[directChannelIndex] : null;

		if (returnChannel) {
			return returnChannel;
		}
	}
	return null;
}

/**
 * Some channels do not have analogInputUse mode, so we need to derive it from the corresponding input channel
 * @param device Device object with audioChannels that contain analogInputUse
 * @param channelId ChannelId of the direct channel that doesn't have analogInputUse
 * @returns will return analogInputUse mode from the corresponding input channel
 */
export function getAnalogInputUseFromOriginChannel(
	device: SiblingChannelsDevice,
	channelId: string
): ChannelAnalogInputUseMode | undefined {
	const originChannel = getCorrespondingInputChannel(device, channelId);
	return originChannel?.features.analogInputUse?.mode;
}
